import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/utils/api-client';
import { Booking, ContactInfo } from '@/types';

// Query keys
export const bookingKeys = {
  all: ['bookings'] as const,
  lists: () => [...bookingKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...bookingKeys.lists(), { filters }] as const,
  details: () => [...bookingKeys.all, 'detail'] as const,
  detail: (id: string) => [...bookingKeys.details(), id] as const,
  byListing: (listingId: string) => [...bookingKeys.all, 'byListing', listingId] as const,
};

// Booking form interface
export interface BookingForm {
  listingId: string;
  startDate: string;
  endDate: string;
  guests: number;
  guestName: string;
  guestEmail: string;
  totalPrice: number;
  primaryGuestContact: ContactInfo;
  expectedGuests: number;
}

// Get all user bookings
export const useBookings = (filters?: { 
  status?: string; 
  listingId?: string; 
  startDate?: string; 
  endDate?: string; 
}) => {
  return useQuery({
    queryKey: bookingKeys.list(filters || {}),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.listingId) params.append('listingId', filters.listingId);
      if (filters?.startDate) params.append('startDate', filters.startDate);
      if (filters?.endDate) params.append('endDate', filters.endDate);
      
      const endpoint = `/bookings${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await apiClient.get<Booking[]>(endpoint);
      return response.data;
    },
    staleTime: 1 * 60 * 1000, // 1 minute (bookings change frequently)
    cacheTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Get bookings for a specific listing
export const useListingBookings = (listingId: string) => {
  return useQuery({
    queryKey: bookingKeys.byListing(listingId),
    queryFn: async () => {
      const response = await apiClient.get<Booking[]>(`/bookings?listingId=${listingId}`);
      return response.data;
    },
    enabled: !!listingId,
    staleTime: 1 * 60 * 1000, // 1 minute
    cacheTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Get single booking
export const useBooking = (id: string) => {
  return useQuery({
    queryKey: bookingKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get<Booking>(`/bookings/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

// Create new booking
export const useCreateBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BookingForm) => {
      const response = await apiClient.post<Booking>('/bookings', data);
      return response.data;
    },
    onSuccess: (newBooking) => {
      // Add to the bookings cache
      queryClient.setQueryData<Booking[]>(
        bookingKeys.list({}),
        (old) => old ? [newBooking, ...old] : [newBooking]
      );
      
      // Add to listing-specific bookings cache
      queryClient.setQueryData<Booking[]>(
        bookingKeys.byListing(newBooking.listingId),
        (old) => old ? [newBooking, ...old] : [newBooking]
      );
      
      // Set individual booking cache
      queryClient.setQueryData(bookingKeys.detail(newBooking.bookingId), newBooking);
    },
    onSettled: () => {
      // Invalidate bookings queries
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
    },
  });
};

// Update booking
export const useUpdateBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { 
      id: string; 
      data: Partial<Pick<Booking, 'status' | 'paymentStatus' | 'startDate' | 'endDate' | 'guests'>>
    }) => {
      const response = await apiClient.put<Booking>(`/bookings/${id}`, data);
      return response.data;
    },
    onMutate: async ({ id, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: bookingKeys.detail(id) });

      // Snapshot the previous value
      const previousBooking = queryClient.getQueryData<Booking>(bookingKeys.detail(id));

      // Optimistically update to the new value
      if (previousBooking) {
        const updatedBooking = {
          ...previousBooking,
          ...data,
          updatedAt: new Date().toISOString(),
        };
        queryClient.setQueryData<Booking>(bookingKeys.detail(id), updatedBooking);
        
        // Update in bookings list as well
        queryClient.setQueryData<Booking[]>(
          bookingKeys.list({}),
          (old) => old ? old.map(booking => 
            booking.bookingId === id ? updatedBooking : booking
          ) : []
        );
        
        // Update in listing-specific bookings
        queryClient.setQueryData<Booking[]>(
          bookingKeys.byListing(updatedBooking.listingId),
          (old) => old ? old.map(booking => 
            booking.bookingId === id ? updatedBooking : booking
          ) : []
        );
      }

      return { previousBooking };
    },
    onError: (err, { id }, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousBooking) {
        queryClient.setQueryData(bookingKeys.detail(id), context.previousBooking);
      }
    },
    onSettled: (data, error, { id }) => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: bookingKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      if (data) {
        queryClient.invalidateQueries({ queryKey: bookingKeys.byListing(data.listingId) });
      }
    },
  });
};

// Cancel booking
export const useCancelBooking = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, listingId }: { id: string; listingId?: string }) => {
      const url = listingId ? `/bookings/${id}?listingId=${listingId}` : `/bookings/${id}`;
      const response = await apiClient.put<{ message: string; booking: Booking }>(url, { status: 'cancelled' });
      return response.data;
    },
    onMutate: async ({ id }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: bookingKeys.detail(id) });

      // Snapshot the previous value
      const previousBooking = queryClient.getQueryData<Booking>(bookingKeys.detail(id));

      // Optimistically update to cancelled status
      if (previousBooking) {
        const cancelledBooking = {
          ...previousBooking,
          status: 'cancelled' as const,
          updatedAt: new Date().toISOString(),
          cancelledAt: new Date().toISOString(),
        };
        queryClient.setQueryData<Booking>(bookingKeys.detail(id), cancelledBooking);
        
        // Update in bookings list as well
        queryClient.setQueryData<Booking[]>(
          bookingKeys.list({}),
          (old) => old ? old.map(booking => 
            booking.bookingId === id ? cancelledBooking : booking
          ) : []
        );
        
        // Update in listing-specific bookings
        queryClient.setQueryData<Booking[]>(
          bookingKeys.byListing(cancelledBooking.listingId),
          (old) => old ? old.map(booking => 
            booking.bookingId === id ? cancelledBooking : booking
          ) : []
        );
      }

      return { previousBooking };
    },
    onError: (err, { id }, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousBooking) {
        queryClient.setQueryData(bookingKeys.detail(id), context.previousBooking);
      }
    },
    onSettled: (data, error, { id }) => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: bookingKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() });
      if (data?.booking) {
        queryClient.invalidateQueries({ queryKey: bookingKeys.byListing(data.booking.listingId) });
      }
    },
  });
};

